import React from 'react';
import { Headline } from '../../types/Headline';
import { FileText, Hash, Eye, EyeOff } from 'lucide-react';
import { TableColumn } from '../../utils/tableUtils';
import { StatusBadge } from '../shared/AdminTable';

// Define standardized headline table columns
export const headlineColumns: TableColumn<Headline>[] = [
	{
		id: 'displayOrder',
		label: 'Order',
		accessor: (headline) => (
			<div className="flex items-center gap-1">
				<Hash size={16} className="text-gray-500" />
				<span className="font-mono text-sm">{headline.displayOrder}</span>
			</div>
		),
		sortable: true,
	},
	{
		id: 'title',
		label: 'Title',
		accessor: (headline) => (
			<div className="flex items-center gap-2">
				<FileText size={16} className="text-blue-500" />
				<span className="font-medium">{headline.title}</span>
			</div>
		),
		sortable: true,
	},
	{
		id: 'content',
		label: 'Content',
		accessor: (headline) => (
			<div className="max-w-xs truncate" title={headline.content}>
				{headline.content}
			</div>
		),
		sortable: true,
	},
	{
		id: 'active',
		label: 'Status',
		accessor: (headline) => (
			<StatusBadge
				status={headline.active ? 'Active' : 'Inactive'}
				type="active"
			/>
		),
		sortable: true,
	},
	{
		id: 'createdAt',
		label: 'Created',
		accessor: (headline) => (
			<span className="text-sm text-gray-500">
				{headline.createdAt ? new Date(headline.createdAt).toLocaleDateString() : 'N/A'}
			</span>
		),
		sortable: true,
	},
	{
		id: 'actions',
		label: 'Actions',
		accessor: () => null, // Actions are handled by the AdminTable component
		sortable: false,
	},
];

// Configuration object for the AdminTable component
export const headlineTableConfig = {
	entityName: 'Headline',
	apiEndpoint: '/api/admin/headlines',
	columns: headlineColumns,
	searchFields: ['title', 'content'] as (keyof Headline)[],
	filterOptions: {
		active: [
			{ value: 'true', label: 'Active' },
			{ value: 'false', label: 'Inactive' }
		]
	},
	bulkActions: [
		{
			id: 'activate',
			label: 'Activate Selected',
			icon: <Eye size={16} />,
			variant: 'success' as const,
		},
		{
			id: 'deactivate',
			label: 'Deactivate Selected',
			icon: <EyeOff size={16} />,
			variant: 'secondary' as const,
		},
	],
	defaultSort: {
		field: 'displayOrder' as keyof Headline,
		direction: 'asc' as 'asc' | 'desc'
	},
	exportFileName: 'headlines',
	defaultVisibleColumns: {
		displayOrder: true,
		title: true,
		content: true,
		active: true,
		createdAt: true,
		actions: true
	}
};
